import {useTranslation} from 'react-i18next';
import {CopyMessageButton} from './copyMessageButton';
import React, {useEffect, useState} from 'react';
import {Button} from 'react-bootstrap';
import {SvgIcon} from '../../icons/svgIcon';
import {Icons} from '../../icons/icons';
import Markdown from 'markdown-to-jsx';
import {fetchSummary, getUuidForSingleDocument} from './utils';
import {doUpdateSummaryRequest} from '../duck/assistant-actions';
import {useDispatch} from 'react-redux';
import {useSelectedDocContext} from '../../search/search-hooks';
import {getCurrentLanguage} from '../../i18n-service';
import {
    AiAssistantConversation,
    AiAssistantConversationContext,
    DEFAULT_ASSISTANT_CONVERSATION_CONTEXT,
} from './types';
import {useStateConversation} from '../duck/assistant-hooks';
import {HibuTextButton} from '../../common/hibuTextButton/HibuTextButton';

export const ChatSummary = (): React.JSX.Element | null => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const currentLanguage = getCurrentLanguage();

    const selectedDocument = useSelectedDocContext();
    const conversationId: string = selectedDocument
        ? getUuidForSingleDocument(selectedDocument.id)
        : getUuidForSingleDocument('someId');
    const conversation: AiAssistantConversation | undefined = useStateConversation(conversationId);
    const conversationContext: AiAssistantConversationContext =
        conversation?.context ?? DEFAULT_ASSISTANT_CONVERSATION_CONTEXT;

    const [summary, setSummary] = useState<string | undefined>(conversation?.summary);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        setSummary('');
    }, [selectedDocument]);

    useEffect(() => {
        if (conversation?.summary) {
            setSummary(conversation.summary);
        }
    }, [conversation?.summary]);

    if (!selectedDocument || !conversation) {
        return null;
    }

    const fetchSummaryAndUpdateConversation = async () => {
        if (isLoading) return; // ignore extra clicks
        setIsLoading(true);
        if (selectedDocument !== undefined && selectedDocument.id !== '') {
            let fullSummary = '';
            const result = await fetchSummary(selectedDocument, currentLanguage, conversationContext, (token) => {
                fullSummary += token;
                setSummary((prev) => prev + token);
            });
            if (result) {
                dispatch(doUpdateSummaryRequest(conversationId, fullSummary));
                setIsLoading(false);
            }
        }
    };

    return (
        <div className="chat-summary">
            {summary !== '' ? (
                <>
                    <div onClick={() => setExpanded(!expanded)} className={'toggle-header'}>
                        <Button className="toggle-button">
                            <SvgIcon icon={Icons.CHEVRON_DOWN} className={expanded ? 'expanded' : ''}/>
                        </Button>
                        <div className={'toggle-label'}>{t('search.details.hibu-assistant.summary.summary')}</div>
                    </div>
                    {expanded && (
                        <div className="chat-summary-content">
                            <Markdown>{summary ?? ''}</Markdown>
                            {summary && <CopyMessageButton msg={summary}/>}
                        </div>
                    )}
                </>
            ) : (
                <div className={'toggle-summary'}>
                    <HibuTextButton
                        label={t('search.details.hibu-assistant.summary.request')}
                        onClick={() => fetchSummaryAndUpdateConversation()}
                        disabled={isLoading}
                    />
                </div>
            )}
        </div>
    );
};
